import { type FC, type FunctionComponent, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { Outlet, useLocation } from "react-router-dom";
import { useSignOut, useUser } from "@/admin/entities/auth";
import { useSupportRequestsOpenCount } from "@/admin/entities/assistant";
import { MenuSections, PagePaths } from "@/admin/shared/constants";
import type { HeaderProps } from "@/admin/widgets/header";
import type { SidebarProps } from "@/admin/widgets/sidebar";
import { AccountMenu, MultiLevelMenu, UserCard } from "@/admin/shared/ui";

export interface MainLayoutProps {
  header: FunctionComponent<HeaderProps>;
  sidebar: FunctionComponent<SidebarProps>;
}

export const Layout: FC<MainLayoutProps> = ({
  header: Header,
  sidebar: Sidebar,
}) => {
  const { user } = useUser();
  const [signOut, { loading }] = useSignOut();
  const [isShowSideBar, setIsShowSideBar] = useState(false);
  const location = useLocation();

  // Пункты меню это обычные ссылки роутера, сами они ящик не закрывают. На
  // узком экране ящик временный и висел бы поверх уже открытой страницы,
  // поэтому закрываем его на смену адреса. На широком экране ящик
  // постоянный, там это состояние ни на что не влияет.
  useEffect(() => {
    setIsShowSideBar(false);
  }, [location.pathname]);

  const openRequests = useSupportRequestsOpenCount();

  // Кроме опроса раз в минуту счётчик освежается при переходе между страницами.
  useEffect(() => {
    openRequests.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <>
      <Header
        accountMenu={
          <AccountMenu user={user} onSignOut={signOut} loading={loading} />
        }
        onMenuToggle={() => setIsShowSideBar((prev) => !prev)}
      />

      <Sidebar
        isOpen={isShowSideBar}
        userCard={<UserCard user={user} />}
        menu={
          <MultiLevelMenu
            sections={MenuSections}
            badges={{ [PagePaths.assistantRequests]: openRequests.count }}
          />
        }
        onClose={() => setIsShowSideBar(false)}
        onSignOut={signOut}
        signOutLoading={loading}
      />

      <Box
        component="main"
        sx={{
          ml: { xs: 0, lg: "250px" },
        }}
      >
        <Box sx={{ p: 1 }}>
          <Outlet />
        </Box>
      </Box>
    </>
  );
};
